import { displayPercentageDiscount } from "@website/accommodation-ui/helpers/calculatePercentageDiscount";
import { useHotelReserveCardFooter } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-footer/hotel-reserve-card-footer.hooks";
import { HotelReservedCardFooterTypesPropertiesType } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-footer/hotel-reserved-card-footer.types";
import { HotelSnappPayBadge } from "@website/accommodation-ui/hotel-snapp-pay";
import { Button } from "@website/ui/button";
import { CounterButton } from "@website/ui/counter-button";
import { MiniChips } from "@website/ui/mini-chips";
import { isChannel } from "@website/utils/channel/helpers";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { SnappProDiscountChip } from "@website/website-ui/snapp-pro-discount-chip";
import { useRouter } from "next/router";
import { memo } from "react";

const Component = (properties: HotelReservedCardFooterTypesPropertiesType) => {
  const {
    availableRooms,
    price,
    id,
    nightCount,
    minStay = 1,
    priceOff,
    rackIncluded,
    onRacksClick,
    view = "default",
    snappPayData,
    roomDetail,
    hasSnappProDiscount
  } = properties;
  const router = useRouter();
  const {
    roomCount,
    setRoomCount,
    roomSelect,
    onHandleClick,
    loading,
    openDatePicker,
    handleRecommendedHotel
  } = useHotelReserveCardFooter(properties);

  return (
    <div
      className={`flex items-center justify-between pb-4 ${
        view === "mobile"
          ? "flex-col pt-3"
          : "flex-col pt-3 lg:min-h-[68px] lg:flex-row lg:px-4 lg:pt-4"
      }`}
    >
      <div className="flex w-full flex-row flex-wrap items-center justify-between">
        <div
          className={`${
            view === "mobile"
              ? "hidden"
              : "text-caption hidden lg:order-1 lg:me-4 lg:block"
          }`}
        >
          تعداد اتاق
        </div>
        <div
          className={`text-caption mr-4 ltr:ml-4 ${
            view === "mobile" ? "" : "lg:order-4 lg:m-0"
          }`}
        >
          {`قیمت برای ${numberConvert(nightCount, {
            locale: router.locale
          })} شب`}
        </div>
        <div
          className={`text-subtitle-1 ml-4 ltr:mr-4 ${
            view === "mobile" ? "" : "lg:order-5 lg:mr-4 lg:ltr:ml-0"
          }`}
        >
          {price * roomCount > priceOff * roomCount && (
            <div className="flex flex-row items-center justify-end gap-2">
              <span className="text-body-2 text-on-surface-medium-emphasis line-through">
                {numberConvert(numberSeparator(price * roomCount), {
                  locale: router.locale
                })}
              </span>
              {hasSnappProDiscount ? (
                <SnappProDiscountChip
                  percentage={displayPercentageDiscount(price, priceOff)}
                />
              ) : (
                <MiniChips
                  color="primary"
                  text={displayPercentageDiscount(
                    price * roomCount,
                    priceOff * roomCount
                  )}
                  variant="contained"
                />
              )}
            </div>
          )}
          <div>
            {numberConvert(
              numberSeparator(
                price * roomCount - priceOff * roomCount
                  ? priceOff * roomCount
                  : price * roomCount
              ),
              {
                locale: router.locale
              }
            )}
            <span className="text-subtitle-2 px-1">تومان</span>
          </div>
        </div>
        <div
          className={`bg-outline-thin mb-4 mt-3 block h-px w-full ${
            view === "mobile" ? "" : "lg:hidden"
          } ${
            snappPayData?.isEligible && snappPayData?.showDownPayment
              ? "hidden"
              : ""
          }`}
        />
        {snappPayData?.isEligible && snappPayData?.showDownPayment && (
          <div className="flex-auto basis-full py-4 lg:order-3 lg:basis-0 lg:px-4 lg:py-0">
            {!isChannel(["ads"]) && (
              <HotelSnappPayBadge
                {...snappPayData}
                page="hotelPage"
                price={priceOff * roomCount || price * roomCount}
              />
            )}
          </div>
        )}
        <CounterButton
          className={`ms-4 !h-[36px] gap-0 ${
            view === "mobile" ? "" : "lg:order-2 lg:m-0"
          } ${
            view === "mobile" ||
            (snappPayData?.isEligible && snappPayData?.showDownPayment)
              ? ""
              : "lg:flex-auto"
          }`}
          color="secondary"
          decreaseDisabled={roomCount <= 1}
          increaseDisabled={roomCount >= availableRooms}
          locale="fa"
          onChange={setRoomCount}
          size="small"
          value={roomCount}
          variant="outline"
        />
        {Number(nightCount) >= Number(minStay) ? (
          rackIncluded && !roomDetail?.showRoomDetail ? (
            <Button
              className={
                view === "mobile"
                  ? "ml-4 w-[133px] ltr:mr-4"
                  : "ml-4 w-[133px] lg:order-6 lg:col-span-2 lg:m-0 lg:w-[94px] ltr:mr-4"
              }
              onClick={onRacksClick}
              size="small"
            >
              {`رزرو ${numberConvert(roomCount, {
                locale: router.locale
              })} اتاق`}
            </Button>
          ) : (
            <Button
              className={
                view === "mobile"
                  ? "ml-4 w-[133px] ltr:mr-4"
                  : "ml-4 w-[133px] lg:order-6 lg:col-span-2 lg:m-0 lg:w-[94px] ltr:mr-4"
              }
              loading={id === roomSelect ? loading : false}
              onClick={onHandleClick}
              size="small"
            >
              {`رزرو ${numberConvert(roomCount, {
                locale: router.locale
              })} اتاق`}
            </Button>
          )
        ) : (
          <div
            className={`order-6 mt-2 flex w-full flex-col px-4 ${
              view === "mobile" ? "" : "lg:flex-row lg:items-center lg:px-0"
            }`}
          >
            <div
              className={`text-body-2 text-primary mb-2 flex-auto ${
                view === "mobile" ? "" : "lg:m-0"
              }`}
            >
              {minStay === 20
                ? "این اتاق در تاریخ ورود انتخابی شما پذیرش ندارد لطفا تاریخ اقامت خود را تغییر دهید"
                : `حداقل ${numberConvert(minStay, {
                    locale: router.locale
                  })} شب اقامت`}
            </div>
            <div className="flex flex-row gap-2">
              <Button
                className={view === "mobile" ? "w-full" : "w-full lg:w-[94px]"}
                onClick={openDatePicker}
                size="small"
              >
                تغییر تاریخ
              </Button>
              <Button
                className={view === "mobile" ? "w-full" : "w-full lg:w-[122px]"}
                onClick={handleRecommendedHotel}
                size="small"
              >
                هتل های مشابه
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export const HotelReserveCardFooter = memo(Component, deepCompare);
